import { useGarden } from '../context/GardenContext'
import { usePlantCatalog } from '../context/PlantCatalogContext'
import { CONTAINER_SIZE_INFO } from '../types'

export default function ShoppingList() {
  const { state } = useGarden()
  const { getPlantById } = usePlantCatalog()

  const allPlantIds = [
    ...new Set([
      ...state.containers.flatMap(c => c.plantIds),
      ...state.savedPlantIds,
    ])
  ]
  const allPlants = allPlantIds.map(id => getPlantById(id)).filter(Boolean)

  const containersBySize = state.containers.reduce<Record<string, number>>((acc, c) => {
    acc[c.size] = (acc[c.size] ?? 0) + 1
    return acc
  }, {})

  const soilQts = state.containers.reduce((sum, c) => {
    const vol: Record<string, number> = { small: 1, medium: 2, large: 4, xlarge: 6 }
    return sum + (vol[c.size] ?? 2)
  }, 0)

  function handlePrint() { window.print() }

  if (allPlants.length === 0 && state.containers.length === 0) {
    return (
      <div className="ac-card text-center py-8">
        <p className="text-3xl mb-2">🛒</p>
        <p className="font-body text-ac-brown-light">Your shopping list will appear here once you add plants or containers.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-pixel text-ac-brown text-base">🛒 Shopping List</h2>
        <button className="ac-btn-outline text-sm" onClick={handlePrint}>🖨️ Print</button>
      </div>

      {/* Plants */}
      {allPlants.length > 0 && (
        <div className="ac-card">
          <h3 className="font-body font-extrabold text-ac-brown mb-3">🌸 Plants to Buy</h3>
          <div className="space-y-2">
            {allPlants.map(p => (
              <div key={p!.id} className="flex items-center gap-3 py-2 border-b border-dashed border-ac-bg last:border-0">
                <span className="text-xl">{p!.emoji}</span>
                <div className="flex-1">
                  <p className="font-body font-bold text-ac-brown text-sm">{p!.name}</p>
                  <p className="font-body text-xs text-ac-brown-light italic">{p!.scientificName}</p>
                  <p className="font-body text-xs text-ac-brown-light capitalize">
                    {p!.role.join(' / ')} · {p!.minContainerSize}+ pot
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-body text-xs text-ac-green font-bold">
                    {p!.careLevel === 'easy' ? '✅ Great for beginners' : p!.careLevel === 'moderate' ? '🌿 Some care needed' : '⚡ Advanced'}
                  </p>
                  <p className="font-body text-xs text-ac-brown-light">
                    {p!.careLevel === 'easy' ? '🏪 Big-box or nursery' : '🌿 Local nursery recommended'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Containers */}
      {state.containers.length > 0 && (
        <div className="ac-card">
          <h3 className="font-body font-extrabold text-ac-brown mb-3">🪴 Pots & Containers</h3>
          <div className="space-y-2">
            {Object.entries(containersBySize).map(([size, qty]) => {
              const info = CONTAINER_SIZE_INFO[size as keyof typeof CONTAINER_SIZE_INFO]
              return (
                <div key={size} className="flex justify-between items-center py-1 border-b border-dashed border-ac-bg last:border-0">
                  <div>
                    <p className="font-body font-bold text-ac-brown text-sm">{info.label}</p>
                    <p className="font-body text-xs text-ac-brown-light">{info.inches} · min {info.depth} deep · drainage holes required</p>
                  </div>
                  <span className="font-pixel text-ac-brown text-xs bg-ac-yellow px-2 py-1 rounded-lg">×{qty}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Supplies */}
      <div className="ac-card">
        <h3 className="font-body font-extrabold text-ac-brown mb-3">🛍️ Supplies</h3>
        <ul className="space-y-2 font-body text-sm text-ac-brown">
          <li className="flex justify-between">
            <span>🌱 Potting mix (NOT garden soil)</span>
            <span className="font-bold text-ac-brown-light">~{soilQts} qt</span>
          </li>
          <li>🫙 Drainage saucers — one per pot</li>
          <li>💦 Liquid balanced fertilizer (10-10-10)</li>
          <li>🌸 Bloom booster fertilizer (for flowering plants)</li>
          <li>🪝 Trowel or small hand shovel</li>
          <li>🚿 Watering can or hose with gentle nozzle</li>
          {state.containers.some(c => c.location === 'balcony') && (
            <li className="text-ac-brown-light text-xs">⚠️ Balcony: check weight limits — large pots can be very heavy when wet</li>
          )}
        </ul>
      </div>

      <p className="font-body text-xs text-ac-brown-light text-center italic">
        Tip: Visit a local nursery for the best selection and expert advice for your exact region!
      </p>
    </div>
  )
}
